import { Link, Stack } from '@mui/material';
import { Bookmark } from 'components/common/Bookmark/Bookmark';
import { StatusTag } from 'components/common/BrandPortalStatusTag';
import { DynamicToolTip } from 'components/common/DynamicToolTip';
import { VerifiedCompanyIcon } from 'components/common/icon/VerifiedCompanyIcon';
import {
  CustomAttributesTypes,
  MultiSelectValue,
  SingleSelectValue,
  UnionCustomAttributeProps
} from 'components/common/Portals/CustomAttributes/types';
import { getItemInfo } from 'helpers/Search';
import { MRT_ColumnDef, MRT_Row } from 'material-react-table';
import { Link as RouterLink } from 'react-router-dom';
import { EventSearchDocument } from 'types/Search';
import { BOOKMARK_SIZE } from '../types';

export const getBrandPortalTableColumns = (
  customAttributesDefinitions: UnionCustomAttributeProps[] | undefined
): MRT_ColumnDef<EventSearchDocument>[] | undefined => {
  // if we don't have all information, we won't have columns (all or nothing)
  if (!customAttributesDefinitions) return undefined;
  return [
    {
      accessorKey: 'bookmarked',
      enableHiding: false,
      enableColumnOrdering: false,
      visibleInShowHideMenu: false,
      accessorFn: row => row.bookmarked,
      Cell: ({ row }) => (
        <Bookmark
          product={row.original}
          isBookmarked={!!row.original.bookmarked}
        />
      ),
      header: '',
      size: BOOKMARK_SIZE
    },
    {
      accessorKey: 'name',
      enableHiding: false,
      enableColumnOrdering: false,
      visibleInShowHideMenu: false,
      header: 'Ingredient'
    },

    {
      accessorKey: 'attributes',
      header: 'Attributes',
      enableColumnOrdering: false,
      accessorFn: row => getItemInfo(row, false),
      enableHiding: false,
      visibleInShowHideMenu: false,
      Cell: ({ renderedCellValue }) => (
        <DynamicToolTip text={renderedCellValue} lineClamp={2} />
      )
    },
    {
      accessorKey: 'company.name',
      header: 'Company',
      enableColumnOrdering: false,
      enableHiding: false,
      visibleInShowHideMenu: false,
      accessorFn: row => row.company.name,
      Cell: ({ row, renderedCellValue }) => (
        <Stack direction={'row'} sx={{ alignItems: 'center', gap: 1 }}>
          <Link
            component={RouterLink}
            onClick={e => e.stopPropagation()}
            sx={{
              color: ({ palette }) => palette.text.primary,
              textDecoration: 'none',
              '&:hover': { color: 'primary.main' }
            }}
            to={`/events/${row.original.event.event.key}/company/${row.original.company.key}`}
          >
            {renderedCellValue}
          </Link>
          {row.original.company.verified && <VerifiedCompanyIcon size="sm" />}
        </Stack>
      )
    },
    ...customAttributesDefinitions.map(col => ({
      accessorKey: col.key,
      Cell: ({ row }: { row: MRT_Row<EventSearchDocument> }) => {
        const attribute = row.original.event.product_custom_attributes?.find(
          attribute => attribute.definition.key === col.key
        );

        if (!attribute) {
          return null;
        }
        const value = attribute.value;
        switch (col.type) {
          case CustomAttributesTypes.MULTI_SELECT:
            return (
              <>{(value as MultiSelectValue).map(o => o.label).join(', ')}</>
            );
          case CustomAttributesTypes.SINGLE_SELECT: {
            const v = value as SingleSelectValue;
            return v.color_code ? (
              <StatusTag
                label={{ color: v.color_code, title: v.label }}
                size="small"
              />
            ) : (
              <>{v.label}</>
            );
          }
          case CustomAttributesTypes.TEXT:
            return value ? <>{value}</> : null;
          default:
            return null;
        }
      },
      header: col.name,
      Header: () => {
        return <DynamicToolTip text={col.name} lineClamp={1} />;
      }
    }))
  ];
};

export const dummyConfig: MRT_ColumnDef<EventSearchDocument>[] = [
  { accessorKey: 'name', Header: () => <div>&nbsp;</div>, header: '' }
];
